import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';
import {
  ShieldCheckIcon,
  UserGroupIcon,
  BadgeCheckIcon,
  StarIcon,
  ClockIcon,
  DocumentTextIcon,
  ClipboardCheckIcon,
} from '@heroicons/react/outline';
import LanguageSwitcher from '../common/LanguageSwitcher';

const ApplicationIntro = ({ onNext }) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* The switcher sits above everything else on the very first screen an
            applicant sees. Someone who cannot read English needs to find this
            before they read anything, not after they have given up. */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <ShieldCheckIcon className="h-12 w-12 text-primary-600 mx-auto mb-4" aria-hidden="true" />
          <h1 className="text-3xl font-display font-bold text-gray-900">{t('intro.title')}</h1>
          <p className="mt-2 text-gray-600">{t('intro.subtitle')}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <p className="text-gray-600">{t('intro.lead')}</p>

          <div className="mt-6 divide-y divide-gray-200">
            {/* Who can apply? */}
            <div className="py-6 first:pt-0">
              <div className="flex items-center">
                <UserGroupIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                <h3 className="ml-2 text-lg font-medium text-gray-900">{t('intro.whoCanApply.title')}</h3>
              </div>
              <p className="text-sm text-gray-600 mt-1">{t('intro.whoCanApply.body')}</p>
            </div>

            {/* Do I need a license? */}
            <div className="py-6 last:pb-0">
              <div className="flex items-center">
                <BadgeCheckIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                <h3 className="ml-2 text-lg font-medium text-gray-900">{t('intro.license.title')}</h3>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {/* Trans (rather than three concatenated strings) so translators
                    can move the link to wherever the sentence needs it — word
                    order around a clause varies a lot across these languages. */}
                <Trans
                  i18nKey="intro.license.body"
                  components={{
                    bold: <strong />,
                    fdacs: (
                      <a
                        href="https://licensing.fdacs.gov/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 underline"
                      >
                        {/* children supplied by the translation */}
                      </a>
                    ),
                  }}
                />
              </p>
            </div>

            {/* What are the GUARDLY tiers? */}
            <div className="py-6 last:pb-0">
              <div className="flex items-center">
                <StarIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                <h3 className="ml-2 text-lg font-medium text-gray-900">{t('intro.tiers.title')}</h3>
              </div>
              <ul className="mt-1 space-y-1 list-disc ml-5">
                <li className="text-sm text-gray-600">{t('intro.tiers.tier1')}</li>
                <li className="text-sm text-gray-600">{t('intro.tiers.tier2')}</li>
                <li className="text-sm text-gray-600">{t('intro.tiers.tier3')}</li>
              </ul>
            </div>

            {/* Is this full-time work? */}
            <div className="py-6 last:pb-0">
              <div className="flex items-center">
                <ClockIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                <h3 className="ml-2 text-lg font-medium text-gray-900">{t('intro.fullTime.title')}</h3>
              </div>
              <p className="text-sm text-gray-600 mt-1">{t('intro.fullTime.body')}</p>
            </div>

            {/* What documents are needed? */}
            <div className="py-6 last:pb-0">
              <div className="flex items-center">
                <DocumentTextIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                <h3 className="ml-2 text-lg font-medium text-gray-900">{t('intro.documents.title')}</h3>
              </div>
              <p className="text-sm text-gray-600 mt-1">{t('intro.documents.body')}</p>
            </div>

            {/* What happens after I apply? */}
            <div className="py-6 last:pb-0">
              <div className="flex items-center">
                <ClipboardCheckIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                <h3 className="ml-2 text-lg font-medium text-gray-900">{t('intro.afterApply.title')}</h3>
              </div>
              <p className="text-sm text-gray-600 mt-1">{t('intro.afterApply.body')}</p>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={onNext}
              className="btn btn-primary"
            >
              {t('nav.next')}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ApplicationIntro;
